import CryptoJS from 'crypto-js';

import { IData } from './Models/DataType';
import { IStorage } from './Models/StorageType';

export abstract class Storage<T extends string, Y> {
  private readonly storage: IStorage;
  private readonly key: string;

  public constructor(getStorage = (): IStorage => window.localStorage, getKey = (): string => '$ecret') {
    this.storage = getStorage();
    this.key = getKey();
  }

  protected get(key: T): Y[] | Y | null {
    if (this.hasValue(key)) {
      const obj: IData = JSON.parse(this.storage.getItem(key) ?? '{}');
      // Compare the checksums
      if (obj.checksum === CryptoJS.SHA256(obj.encryptedData).toString()) {
        // Data is not modified, decrypt it
        const decryptedData: Y[] | Y = JSON.parse(
          CryptoJS.AES.decrypt(obj.encryptedData, this.key).toString(CryptoJS.enc.Utf8)
        );
        return decryptedData;
      } else {
        // Data is modified, handle the error
        console.error(typeof obj.encryptedData + ' is corrupted');
        return null;
      }
    } else return null;
  }

  protected getWithExpire(key: T): Y[] | Y | null {
    if (!this.isExpire(key)) {
      const obj: IData = JSON.parse(this.storage.getItem(key) ?? '{}');
      // Compare the checksums
      if (obj.checksum === CryptoJS.SHA256(obj.encryptedData).toString()) {
        // Data is not modified, decrypt it
        const decryptedData: Y[] | Y = JSON.parse(
          CryptoJS.AES.decrypt(obj.encryptedData, this.key).toString(CryptoJS.enc.Utf8)
        );
        return decryptedData;
      } else {
        // Data is modified, handle the error
        console.error(typeof obj.encryptedData + ' is corrupted');
        return null;
      }
    } else return null;
  }

  protected set(key: T, value: Y[] | Y): void {
    const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(value), this.key).toString();
    const checksum = CryptoJS.SHA256(encryptedData).toString();
    this.storage.setItem(
      key,
      JSON.stringify({
        encryptedData: encryptedData,
        checksum: checksum,
      })
    );
  }

  protected setWithExpire(key: T, value: Y[] | Y, expiry: Date): void {
    const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(value), this.key).toString();
    const checksum = CryptoJS.SHA256(encryptedData).toString();
    this.storage.setItem(
      key,
      JSON.stringify({
        encryptedData: encryptedData,
        checksum: checksum,
        expiry: expiry.getTime(),
      })
    );
  }

  protected clearItem(key: T): void {
    this.storage.removeItem(key);
  }

  protected clearItems(keys: T[]): void {
    keys.forEach((key) => this.clearItem(key));
  }

  protected hasValue(key: T): boolean {
    return this.storage.getItem(key) ? true : false;
  }

  protected isExpire(key: T): boolean {
    if (this.hasValue(key)) {
      const obj: IData = JSON.parse(this.storage.getItem(key) ?? '{}');
      if (obj.expiry && new Date().getTime() > obj.expiry) return true;
      else return false;
    }
    return true;
  }
}
